/**
 * Utility functions for validating URL accessibility
 */

export interface UrlValidationResult {
  isAccessible: boolean;
  error?: string;
}

/**
 * Check if a URL is accessible by making a HEAD request
 * For images, we check if it's actually an image
 * For audio/video URLs, we check if the resource is accessible
 */
export async function checkUrlAccessibility(
  url: string,
  type?: 'image' | 'audio' | 'video'
): Promise<UrlValidationResult> {
  if (!url || url.trim() === '') {
    return { isAccessible: false, error: 'Empty URL' };
  }

  // Basic URL validation
  try {
    new URL(url);
  } catch {
    return { isAccessible: false, error: 'Invalid URL format' };
  }

  // For YouTube and TikTok videos, just validate the format
  if (type === 'video') {
    const videoPattern = /^https?:\/\/(www\.)?(youtube\.com\/watch\?v=[\w-]+|youtube\.com\/shorts\/[\w-]+|youtu\.be\/[\w-]+|tiktok\.com\/@[\w.-]+\/video\/\d+|vm\.tiktok\.com\/[\w]+)/;
    if (videoPattern.test(url)) {
      return { isAccessible: true };
    }
    return { isAccessible: false, error: 'Invalid YouTube or TikTok URL' };
  }

  try {
    // Use fetch with HEAD method to check if URL is accessible without downloading the full content
    const controller = new AbortController();
    const timeoutId = setTimeout(() => controller.abort(), 5000); // 5 second timeout

    await fetch(url, {
      method: 'HEAD',
      signal: controller.signal,
      mode: 'no-cors', // Allow checking external URLs
    });

    clearTimeout(timeoutId);

    // In no-cors mode, we can't read the response, but if fetch succeeds, the URL is accessible
    // We'll consider it accessible if fetch doesn't throw an error
    return { isAccessible: true };
  } catch (error) {
    // If HEAD fails, try GET with a small range to verify accessibility
    try {
      const controller = new AbortController();
      const timeoutId = setTimeout(() => controller.abort(), 5000);

      await fetch(url, {
        method: 'GET',
        signal: controller.signal,
        mode: 'no-cors',
      });

      clearTimeout(timeoutId);
      return { isAccessible: true };
    } catch {
      return { 
        isAccessible: false, 
        error: error instanceof Error ? error.message : 'URL not accessible'
      };
    }
  }
}

/**
 * Debounce function to limit the rate of URL checks
 */
export function debounce<T extends (...args: any[]) => any>(
  func: T,
  wait: number
): (...args: Parameters<T>) => void {
  let timeout: number | null = null;
  
  return function executedFunction(...args: Parameters<T>) {
    const later = () => {
      timeout = null;
      func(...args);
    };
    
    if (timeout) {
      clearTimeout(timeout);
    }
    timeout = window.setTimeout(later, wait);
  };
}
